// ===============================================================
// 💠 ConvertTokensToCoinsPage — تحويل التوكنز إلى كوينز
// path: src/pages/withdraw/ConvertTokensToCoinsPage.js
// ===============================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth, db } from "../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import TopBar from "../../components/common/TopBar";
import coinsGold from "../../assets/gold.png";
import tokenImg from "../../assets/token.png";

import "./ConvertTokensToCoinsPage.css";

// ✅ السعر المعتمد
// 50 Token = 100 Coins
const TOKEN_PACKAGES = [
  { id: "t10", tokens: 10, coins: 20 },
  { id: "t25", tokens: 25, coins: 50 },
  { id: "t50", tokens: 50, coins: 100 },
  { id: "t100", tokens: 100, coins: 200 },
  { id: "t200", tokens: 200, coins: 400 },
  { id: "t500", tokens: 500, coins: 1000 },
];

export default function ConvertTokensToCoinsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tokenBalance, setTokenBalance] = useState(0);
  const [coinsBalance, setCoinsBalance] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() || {};
      setTokenBalance(Number(data.earnings ?? 0));
      setCoinsBalance(Number(data.coins ?? 0));
    });

    return () => unsub();
  }, []);

  const selectedPack = TOKEN_PACKAGES.find((p) => p.id === selectedId);

  const canConvert =
    selectedPack &&
    Number(selectedPack.tokens) <= Number(tokenBalance) &&
    !converting;

  const handleSelect = (pkg) => {
    if (pkg.tokens > tokenBalance) return;
    setSelectedId(pkg.id);
  };

  const handleConvert = async () => {
    if (!canConvert) return;

    try {
      setConverting(true);

      const functions = getFunctions();
      const convertFn = httpsCallable(functions, "convertTokensToCoins");

      const payload = {
        tokensToDeduct: selectedPack.tokens,
      };

      const result = await convertFn(payload);
      console.log("convert tokens -> coins result", result?.data);

      // لا حاجة للتنقل فورًا، لأن onSnapshot سيحدث الأرصدة مباشرة
      setSelectedId(null);
    } catch (err) {
      console.error(err);
      alert(
        err?.message ||
          t("convertTokens.error_generic", {
            defaultValue: "حدث خطأ أثناء التحويل",
          })
      );
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="convertcoins-container">
      <TopBar
        title={t("convertTokens.title", { defaultValue: "تحويل التوكنز" })}
        onBack={() => navigate(-1)}
      />

      {/* الرصيدين */}
      <div className="convert-balances-wrap">
        <div className="convert-balance-box">
          <img
            src={tokenImg}
            alt={t("assets.token_alt", { defaultValue: "token" })}
            className="convert-balance-icon-img"
          />
          <span className="convert-balance-value">{tokenBalance}</span>
          <span className="convert-balance-label">
            {t("convertTokens.tokensLabel", { defaultValue: "توكنز" })}
          </span>
        </div>

        <div className="convert-balance-box">
          <img
            src={coinsGold}
            alt={t("assets.coins_alt", { defaultValue: "coins" })}
            className="convert-balance-icon-img"
          />
          <span className="convert-balance-value">{coinsBalance}</span>
          <span className="convert-balance-label">
            {t("convertTokens.coinsLabel", { defaultValue: "كوينز" })}
          </span>
        </div>
      </div>

      {/* سعر التحويل */}
      <div className="convert-rate">
        {t("convertTokens.rateLine", { defaultValue: "50 Token = 100 Coins" })}
      </div>

      {/* الباقات */}
      <div className="convert-grid">
        {TOKEN_PACKAGES.map((p) => {
          const disabled = p.tokens > tokenBalance;
          const isSelected = selectedId === p.id;

          return (
            <div
              key={p.id}
              className={`convert-card ${isSelected ? "is-selected" : ""} ${
                disabled ? "is-disabled" : ""
              }`}
              onClick={() => handleSelect(p)}
              role="button"
              tabIndex={0}
              aria-disabled={disabled}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !disabled) {
                  handleSelect(p);
                }
              }}
            >
              <div className="convert-card-top">
                <div className="convert-values">
                  <span className="convert-from">{p.tokens}</span>
                  <span className="convert-arrow">→</span>
                  <span className="convert-to">{p.coins}</span>
                </div>

                <div className="convert-units-row">
                  <div className="convert-unit">
                    <img
                      src={tokenImg}
                      alt={t("assets.token_alt", { defaultValue: "token" })}
                      className="convert-small-icon"
                    />
                    <span className="convert-icon-label">
                      {t("convertTokens.tokenUnit", { defaultValue: "توكن" })}
                    </span>
                  </div>

                  <div className="convert-unit">
                    <img
                      src={coinsGold}
                      alt={t("assets.coins_alt", { defaultValue: "coins" })}
                      className="convert-small-icon"
                    />
                    <span className="convert-icon-label">
                      {t("convertTokens.coinsLabel", { defaultValue: "كوينز" })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="convert-bottom">
                {disabled
                  ? t("convertTokens.insufficientBalance", {
                      defaultValue: "الرصيد غير كافٍ",
                    })
                  : t("convertTokens.summary", {
                      defaultValue: "{{tokens}} توكن → {{coins}} كوينز",
                      tokens: p.tokens,
                      coins: p.coins,
                    })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ملخص التحويل */}
      {selectedPack && (
        <div className="convert-summary">
          {t("convertTokens.summary", {
            defaultValue: "{{tokens}} Token → {{coins}} Coins",
            tokens: selectedPack.tokens,
            coins: selectedPack.coins,
          })}
        </div>
      )}

      {/* الزر */}
      <button
        type="button"
        className={`convert-submit ${!canConvert ? "is-disabled" : ""}`}
        onClick={handleConvert}
        disabled={!canConvert}
      >
        {converting
          ? t("convertTokens.converting", { defaultValue: "جارٍ التحويل..." })
          : t("convertTokens.confirm", { defaultValue: "تأكيد التحويل" })}
      </button>

      {/* سطر مساعد */}
      <div className="convert-support">
        {t("convertTokens.helpLine", {
          defaultValue:
            "يتم التحويل من التوكنز إلى الكوينز لاستخدامها داخل التطبيق",
        })}
      </div>
    </div>
  );
}